import React, { useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import { IdCard, Hash, Lock, LogIn, ShieldCheck } from "lucide-react";

const Login = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [birthCertificateNo, setBirthCertificateNo] = useState("");
  const [admissionNo, setAdmissionNo] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login({
        birthCertificateNo,
        admissionNo,
        password,
        type: "STUDENT",
      });
      toast.success("Login successful");
      if (user.role === "STUDENT") {
        navigate("/student-dashboard");
      } else {
        navigate("/admin-dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid login credentials");
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    {
      label: "Birth Certificate Number",
      icon: <IdCard size={18} />,
      value: birthCertificateNo,
      onChange: (e) => setBirthCertificateNo(e.target.value),
      placeholder: "Enter certificate number",
      type: "text",
      name: "bc",
    },
    {
      label: "Admission Number",
      icon: <Hash size={18} />,
      value: admissionNo,
      onChange: (e) => setAdmissionNo(e.target.value),
      placeholder: "Enter admission number",
      type: "text",
      name: "adm",
    },
    {
      label: "Password",
      icon: <Lock size={18} />,
      value: password,
      onChange: (e) => setPassword(e.target.value),
      placeholder: "Enter password",
      type: "password",
      name: "pwd",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-100 rounded-full opacity-40 animate-pulse" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-blue-200 rounded-full opacity-30 animate-pulse" style={{ animationDelay: "1s" }} />
      </div>

      <div
        className="relative bg-white shadow-xl rounded-2xl w-full max-w-md p-8 animate-slide-up"
        style={{ animation: "slideUp 0.5s ease both" }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
            style={{ animation: "bounceIn 0.6s ease both" }}>
            <ShieldCheck size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-primary">Student Login</h1>
          <p className="text-sm text-gray-500 mt-1">Igembe South Bursary Portal</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {fields.map((field, i) => (
            <div
              key={field.name}
              style={{ animation: `slideUp 0.5s ease ${0.1 + i * 0.08}s both` }}
            >
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                {field.label}
              </label>
              <div
                className={`flex items-center border-2 rounded-xl px-3 transition-all duration-200 ${
                  focusedField === field.name
                    ? "border-blue-700 bg-blue-50/40 shadow-sm"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <span className={`mr-2 transition-colors duration-200 ${focusedField === field.name ? "text-blue-700" : "text-gray-400"}`}>
                  {field.icon}
                </span>
                <input
                  type={field.type}
                  value={field.value}
                  onChange={field.onChange}
                  onFocus={() => setFocusedField(field.name)}
                  onBlur={() => setFocusedField(null)}
                  className="w-full py-2.5 outline-none bg-transparent text-sm"
                  placeholder={field.placeholder}
                  required
                />
              </div>
            </div>
          ))}

          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-800 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{ animation: "slideUp 0.5s ease 0.35s both" }}
          >
            {loading ? (
              <>
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Signing in...
              </>
            ) : (
              <>
                <LogIn size={18} />
                Login
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 border-t border-gray-200" />
          <span className="text-xs text-gray-400">OR</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        {/* Register + Help */}
        <div
          className="text-center text-sm space-y-2"
          style={{ animation: "slideUp 0.5s ease 0.45s both" }}
        >
          <p className="text-gray-600">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary font-semibold hover:underline">
              Create Account
            </Link>
          </p>
          <Link to="/support" className="block text-gray-500 hover:text-primary transition-colors duration-150">
            Need help? Contact Support
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounceIn {
          0%   { opacity: 0; transform: scale(0.5); }
          70%  { transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Login;